import { Ref, SetupContext } from '@vue/composition-api';
import { GroupDataResponse, GroupDataType, GroupsDataResponse } from '@front-app/services/GroupsApi/GroupDataModel';
import { getGroupDataDashboardData } from '@front-app/services/GroupsApi/GroupsDataApi';
import {
	getAllTimeLineSeries,
	getGainBarSeries,
	getLossBarSeries,
	getPeriodLineSeries,
	getWindowAverageSeries,
	SetSeriesFn,
} from '@front-app/store/modules/echarts/echartGraph';
import { sum } from '@front-app/util/math';
import {
	DataPointName,
	DateData,
	EchartsHookState,
	TimeseriesPoint,
	UseEchartWithData,
} from './model';
import {
	calculateAllData,
	extractDataFromResponse,
	extractWindowedSeries,
	fillMinMax,
} from './util';
import { useTranslate } from '../useTranslate';

const DATA_TYPE = GroupDataType.MEMBERS;
const API_KEYNAME_STR = 'join';

interface Response {
	[DataPointName.MEMBER_JOINS]: GroupDataResponse;
	[DataPointName.MEMBER_LEAVES]: GroupDataResponse;
}

const DATA_POINT_NAMES: [DataPointName.MEMBER_JOINS, DataPointName.MEMBER_LEAVES] = [DataPointName.MEMBER_JOINS, DataPointName.MEMBER_LEAVES];

export const useEchartWithGroupMemberData = (
	state: Ref<EchartsHookState>,
	allData: Ref<DateData[]>,
	setSeries: SetSeriesFn,
	ctx: SetupContext,
): UseEchartWithData => {
	const update = async () => {
		const stateVal = state.value;
		const allResponses: GroupsDataResponse = await getGroupDataDashboardData({
			...stateVal,
			dataType: DATA_TYPE,
		});

		const responses = extractDataFromResponse(stateVal, allResponses);
		if (!responses || responses?.length < 2) {
			// eslint-disable-next-line no-console
			console.error('Bad response');
			return;
		}

		const parsedData: Response = parseResponseData(responses);

		const joinsSeriesData = fillMinMax(
			parsedData[DataPointName.MEMBER_JOINS].dataPoints.map(({ date, count }) => [date.getTime(), count]),
			stateVal.dateTimeStart,
			0,
			stateVal.dateTimeEnd,
			0,
		);
		const leavesSeriesData = fillMinMax(
			parsedData[DataPointName.MEMBER_LEAVES].dataPoints.map(({ date, count }) => [date.getTime(), count]),
			stateVal.dateTimeStart,
			0,
			stateVal.dateTimeEnd,
			0,
		);

		const initialTotal = DATA_POINT_NAMES
			.map((name) => parsedData[name].totalBeforeStart)
			.reduce(sum, 0);

		const [
			allDataVal,
			allDataFilledVal,
			runningTotal,
		] = calculateAllData(DATA_POINT_NAMES, parsedData, initialTotal, stateVal.dateTimeStart, stateVal.dateTimeEnd, stateVal.averagingWindowDays);

		// eslint-disable-next-line no-param-reassign
		allData.value = allDataFilledVal;

		const finalTotalSeries: TimeseriesPoint[] = allDataVal
			.map(({ date, totalAtDate }) => [date.getTime(), totalAtDate]);

		const allTimeTotalSeriesData = fillMinMax(
			finalTotalSeries,
			stateVal.dateTimeStart,
			initialTotal,
			stateVal.dateTimeEnd,
			runningTotal,
		);

		const termTotal = runningTotal - initialTotal;
		const termTotalSeries: TimeseriesPoint[] = allDataVal
			.map(({ date, totalAtDate }) => [date.getTime(), totalAtDate - initialTotal]);

		const termTotalSeriesData = fillMinMax(
			termTotalSeries,
			stateVal.dateTimeStart,
			0,
			stateVal.dateTimeEnd,
			termTotal,
		);

		const windowAverageSeriesData: TimeseriesPoint[] = extractWindowedSeries(stateVal.isEnhancedDashboard, stateVal.averagingWindowDays, allDataVal);

		const $t = useTranslate(ctx);

		setSeries([
			getAllTimeLineSeries($t('data-dashboard.columns.members.total-members') as unknown as string, allTimeTotalSeriesData),
			getPeriodLineSeries($t('data-dashboard.titles.total-members-term') as unknown as string, termTotalSeriesData),
			getGainBarSeries($t('data-dashboard.columns.members.joins') as unknown as string, joinsSeriesData),
			getLossBarSeries($t('data-dashboard.columns.members.leaves') as unknown as string, leavesSeriesData),
			getWindowAverageSeries(stateVal.isEnhancedDashboard, $t('data-dashboard.columns.window-average') as unknown as string, windowAverageSeriesData),
		], stateVal.dateTimeStart, stateVal.dateTimeEnd, runningTotal);
	};
	return {
		update,
	};
};

function parseResponseData(responses: GroupDataResponse[]): Response {
	return responses.reduce((acc: Response, response: GroupDataResponse) => {
		if (response.keyName === API_KEYNAME_STR) {
			acc[DataPointName.MEMBER_JOINS] = response;
		} else {
			acc[DataPointName.MEMBER_LEAVES] = {
				keyName: response.keyName,
				totalBeforeStart: -response.totalBeforeStart,
				dataPoints: response.dataPoints.map(({ date, count }) => ({ date, count: -count })),
			};
		}
		return acc;
	}, {} as Response);
}
